import { formatSecondsToTimestamp } from "@memorang/helpers/common";
import { Pressable, StyleSheet } from "react-native";
import { Text } from "react-native-paper";
import { themeRoundnessMultiplier } from "../../constants";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import BottomSheetModalWrapper from "../BottomSheetModalWrapper";
import Box from "../Box";
import Image from "../CustomImage";
import BottomActionsRow from "./BottomActionsRow";
import DesktopBottomPlayer from "./DesktopBottomPlayer";
import PlaybackControls from "./PlaybackControls";
import SeekBar from "./SeekBar";
import type {
	PodcastPlayerSheetContentProps,
	PodcastPlayerSheetProps,
} from "./types";

export { default as DesktopBottomPlayer } from "./DesktopBottomPlayer";
export type { PodcastPlayerSheetContentProps, PodcastPlayerSheetProps };

const PodcastPlayerSheetContent = ({
	title,
	author,
	artworkUri,
	isPlaying,
	positionSeconds,
	durationSeconds,
	playbackSpeed,
	isBookmarked,
	isInPlaylist,
	isFirstTrack,
	isLastTrack,
	onPlayPause,
	onSkipPrevious,
	onSkipNext,
	onRewind,
	onForward,
	onSeek,
	onToggleBookmark,
	onTogglePlaylist,
	onChangeSpeed,
	onMenuItemPress,
}: PodcastPlayerSheetContentProps) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();

	return (
		<Box
			flex={1}
			flexDirection={isMobile ? "column" : "row"}
			alignItems="center"
			gap={isMobile ? 16 : 32}
			paddingHorizontal={isMobile ? 16 : 32}
			paddingBottom={16}
			paddingTop={8}
			backgroundColor={theme.colors.surface}
			maxWidth={isMobile ? 560 : 900}
			alignSelf="center"
			width="100%"
		>
			<Image
				source={{ uri: artworkUri }}
				accessibilityLabel={`${title} artwork`}
				style={[
					isMobile ? styles.artwork : styles.artworkDesktop,
					{
						shadowColor: theme.colors.shadow,
					},
				]}
				contentFit="cover"
			/>

			<Box flex={1} gap={16} width="100%">
				<Box gap={8} width="100%" paddingVertical={8}>
					<Text
						variant="titleMedium"
						numberOfLines={2}
						style={{ color: theme.colors.onSurface }}
					>
						{title}
					</Text>
					<Text
						variant="bodySmall"
						style={{ color: theme.colors.onSurfaceVariant }}
					>
						{author}
					</Text>
				</Box>

				<Box gap={4} width="100%">
					<SeekBar
						variant="full"
						position={positionSeconds}
						duration={durationSeconds}
						onSeek={onSeek}
					/>
					<Box flexDirection="row" justifyContent="space-between" width="100%">
						<Text
							variant="bodySmall"
							style={{ color: theme.colors.onSurfaceVariant }}
						>
							{formatSecondsToTimestamp(positionSeconds)}
						</Text>
						<Text
							variant="bodySmall"
							style={{ color: theme.colors.onSurfaceVariant }}
						>
							{formatSecondsToTimestamp(durationSeconds)}
						</Text>
					</Box>
				</Box>

				<PlaybackControls
					isPlaying={isPlaying}
					isFirstTrack={isFirstTrack}
					isLastTrack={isLastTrack}
					onPlayPause={onPlayPause}
					onSkipPrevious={onSkipPrevious}
					onSkipNext={onSkipNext}
					onRewind={onRewind}
					onForward={onForward}
				/>

				<Box style={{ marginTop: "auto" }} width="100%">
					<BottomActionsRow
						playbackSpeed={playbackSpeed}
						isBookmarked={isBookmarked}
						isInPlaylist={isInPlaylist}
						onChangeSpeed={onChangeSpeed}
						onTogglePlaylist={onTogglePlaylist}
						onToggleBookmark={onToggleBookmark}
						onMenuItemPress={onMenuItemPress}
					/>
				</Box>
			</Box>
		</Box>
	);
};

export const PodcastPlayerSheet = ({
	showModal,
	onDismiss,
	...contentProps
}: PodcastPlayerSheetProps) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();

	if (typeof showModal === "boolean") {
		if (!isMobile) {
			if (!showModal) {
				return null;
			}
			return <DesktopBottomPlayer {...contentProps} />;
		}

		return (
			<BottomSheetModalWrapper
				showModal={showModal}
				onDismiss={onDismiss}
				enablePanDownToClose
				enableDismissOnClose
				snapPoints={["92.5%"]}
				snapIndex={0}
				backdropPressBehavior="close"
				backdropOpacity={0.32}
				backdropAppearsOnIndex={0}
				backdropDisappearsOnIndex={-1}
				contentContainerStyle={styles.modalContentContainer}
				backgroundStyle={[
					styles.sheetBackground,
					{
						backgroundColor: theme.colors.surface,
						borderWidth: 0,
						borderTopLeftRadius: theme.roundness * themeRoundnessMultiplier,
						borderTopRightRadius: theme.roundness * themeRoundnessMultiplier,
					},
				]}
				style={[
					styles.sheetShadow,
					{
						shadowColor: theme.colors.shadow,
						minHeight: "92.5%",
						borderTopLeftRadius: theme.roundness * themeRoundnessMultiplier,
						borderTopRightRadius: theme.roundness * themeRoundnessMultiplier,
					},
				]}
				customHandle={(handlePress) => (
					<Pressable
						onPress={handlePress}
						style={styles.dragHandleHitArea}
						accessibilityRole="button"
						accessibilityLabel="Close player"
					>
						<Box
							width={32}
							height={4}
							borderRadius={100}
							backgroundColor={theme.colors.outline}
						/>
					</Pressable>
				)}
			>
				<PodcastPlayerSheetContent {...contentProps} />
			</BottomSheetModalWrapper>
		);
	}

	return <PodcastPlayerSheetContent {...contentProps} />;
};

const styles = StyleSheet.create({
	artwork: {
		width: "100%",
		maxWidth: 284,
		aspectRatio: 1,
		borderRadius: 7,
		shadowOffset: { width: 0, height: 0 },
		shadowOpacity: 0.1,
		shadowRadius: 32,
		elevation: 8,
	},
	artworkDesktop: {
		width: 280,
		height: 280,
		aspectRatio: 1,
		borderRadius: 7,
		shadowOffset: { width: 0, height: 0 },
		shadowOpacity: 0.1,
		shadowRadius: 32,
		elevation: 8,
	},
	dragHandleHitArea: {
		paddingTop: 16,
		paddingBottom: 16,
		alignItems: "center",
	},
	modalContentContainer: {
		padding: 0,
		paddingBottom: 32,
		flexGrow: 1,
	},
	sheetBackground: {},
	sheetShadow: {
		shadowOffset: { width: 0, height: 0 },
		shadowOpacity: 0.1,
		shadowRadius: 32,
		elevation: 8,
	},
});
